import { supabase } from '@/integrations/supabase/client';

type CoachingSessionMemoryRow = {
  session_id: string;
  agent_id: string | null;
  template_id: string | null;
  session_start: string | null;
  document_title: string | null;
  document_content: unknown;
  readiness_score: number | null;
  scoring_details: unknown;
  coaching_templates?: {
    name?: string | null;
    category?: string | null;
    output_document?: string | null;
  } | null;
};

export type CoachingMemoryItem = {
  sessionId: string;
  sessionDate: string;
  templateName: string;
  outputDocument?: string;
  readinessScore: number | null;
  strengths: string[];
  areasForImprovement: string[];
  keyTakeaways: string[];
  goals: string[];
  commitments: string[];
  unresolvedActions: string[];
  summary: string;
  isSameCoach: boolean;
  isSameTemplate: boolean;
};

export type StructuredCoachingMemory = {
  goals: string[];
  commitments: string[];
  unresolvedActions: string[];
};

export type CoachingMemoryProfile = {
  learnerName: string;
  totalSessionsFound: number;
  items: CoachingMemoryItem[];
};

const MAX_MEMORY_ITEMS = 4;
const MAX_SUMMARY_CHARS = 420;

export function stripMemoryHtml(value: string | null | undefined): string {
  if (!value) return '';
  return value
    .replace(/<[^>]*>/g, ' ')
    .replace(/\{\{[^}]*\}\}/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, ' ')
    .trim();
}

function clip(value: string, max = MAX_SUMMARY_CHARS): string {
  const clean = stripMemoryHtml(value);
  if (clean.length <= max) return clean;
  return `${clean.slice(0, max).replace(/\s+\S*$/, '')}...`;
}

function toText(value: unknown, depth = 0): string {
  if (value == null || depth > 3) return '';
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  if (Array.isArray(value)) {
    return value.map((item) => toText(item, depth + 1)).filter(Boolean).join('; ');
  }
  if (typeof value === 'object') {
    const objectValue = value as Record<string, unknown>;
    const preferred = objectValue.content ?? objectValue.text ?? objectValue.value ?? objectValue.summary;
    if (preferred) return toText(preferred, depth + 1);

    return Object.entries(objectValue)
      .filter(([key]) => !['id', 'type', 'timestamp'].includes(key))
      .map(([key, item]) => {
        const itemText = toText(item, depth + 1);
        return itemText ? `${key}: ${itemText}` : '';
      })
      .filter(Boolean)
      .join('; ');
  }
  return '';
}

function toStringArray(value: unknown): string[] {
  if (!value) return [];
  if (Array.isArray(value)) {
    return value.map((item) => clip(toText(item), 180)).filter(Boolean).slice(0, 4);
  }
  const text = clip(toText(value), 240);
  return text ? [text] : [];
}

function extractDocumentSummary(documentContent: unknown): string {
  if (!documentContent || typeof documentContent !== 'object') return '';

  const content = documentContent as Record<string, unknown>;
  const sections = Array.isArray(content.sections) ? content.sections : [];
  if (sections.length > 0) {
    return clip(
      sections
        .slice(0, 3)
        .map((section) => toText(section))
        .filter(Boolean)
        .join(' | ')
    );
  }

  return clip(toText(documentContent));
}

export function extractStructuredCoachingMemory(documentContent: unknown): StructuredCoachingMemory {
  if (!documentContent || typeof documentContent !== 'object') {
    return { goals: [], commitments: [], unresolvedActions: [] };
  }

  const content = documentContent as Record<string, unknown>;
  const raw = content.coaching_memory;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return { goals: [], commitments: [], unresolvedActions: [] };
  }

  const memory = raw as Record<string, unknown>;
  return {
    goals: toStringArray(memory.goals),
    commitments: toStringArray(memory.commitments),
    unresolvedActions: toStringArray(memory.unresolved_actions),
  };
}

function formatDate(value: string | null): string {
  if (!value) return 'Previous session';
  try {
    return new Intl.DateTimeFormat('en-AU', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    }).format(new Date(value));
  } catch {
    return 'Previous session';
  }
}

export function getLearnerDisplayName(user?: { name?: string | null; email?: string | null } | null, profile?: { display_name?: string | null; email?: string | null } | null): string {
  const rawName = stripMemoryHtml(profile?.display_name || user?.name || '');
  if (rawName) return rawName.split(/\s+/)[0] || rawName;

  const email = profile?.email || user?.email || '';
  const localPart = email.split('@')[0]?.replace(/[._-]+/g, ' ').trim();
  if (localPart) {
    const [first] = localPart.split(/\s+/);
    return first ? first.charAt(0).toUpperCase() + first.slice(1) : 'there';
  }

  return 'there';
}

export async function fetchCoachingMemoryProfile(options: {
  authUserId?: string | null;
  userId?: string | null;
  learnerName: string;
  currentAgentId?: string | null;
  currentTemplateId?: string | null;
  limit?: number;
}): Promise<CoachingMemoryProfile> {
  const { authUserId, userId, learnerName, currentAgentId, currentTemplateId } = options;
  const requestedLimit = options.limit ?? MAX_MEMORY_ITEMS;

  if (!authUserId && !userId) {
    return { learnerName, totalSessionsFound: 0, items: [] };
  }

  // Memory is scoped to the CURRENT COACH AGENT only. A brand new agent must
  // have no memory even if the learner has completed sessions with other
  // coaches, otherwise the coach fabricates references to unrelated prior
  // work ("Last time we touched on <thing from a different coach>...").
  // Cross-coach context is an opt-in enterprise feature for the future.
  if (!currentAgentId) {
    return { learnerName, totalSessionsFound: 0, items: [] };
  }

  let query = supabase
    .from('coaching_sessions')
    .select(`
      session_id,
      agent_id,
      template_id,
      session_start,
      document_title,
      document_content,
      readiness_score,
      scoring_details,
      coaching_templates (name, category, output_document)
    `)
    .eq('status', 'completed')
    .eq('agent_id', currentAgentId)
    .order('session_start', { ascending: false })
    .limit(12);

  if (authUserId) {
    query = query.eq('auth_user_id', authUserId);
  } else if (userId) {
    query = query.eq('user_id', userId);
  }

  const { data, error } = await query;
  if (error) {
    console.error('[CoachingMemory] Failed to fetch previous coaching sessions:', error);
    return { learnerName, totalSessionsFound: 0, items: [] };
  }

  const rows = (data || []) as CoachingSessionMemoryRow[];
  const rankedRows = [...rows].sort((a, b) => {
    const aSameAgent = currentAgentId && a.agent_id === currentAgentId ? 1 : 0;
    const bSameAgent = currentAgentId && b.agent_id === currentAgentId ? 1 : 0;
    if (aSameAgent !== bSameAgent) return bSameAgent - aSameAgent;

    const aSameTemplate = currentTemplateId && a.template_id === currentTemplateId ? 1 : 0;
    const bSameTemplate = currentTemplateId && b.template_id === currentTemplateId ? 1 : 0;
    if (aSameTemplate !== bSameTemplate) return bSameTemplate - aSameTemplate;

    return new Date(b.session_start || 0).getTime() - new Date(a.session_start || 0).getTime();
  });

  const items = rankedRows.slice(0, requestedLimit).map((session) => {
    const scoring = (session.scoring_details || {}) as Record<string, unknown>;
    const structuredMemory = extractStructuredCoachingMemory(session.document_content);
    return {
      sessionId: session.session_id,
      sessionDate: formatDate(session.session_start),
      templateName: session.coaching_templates?.name || session.document_title || 'Coaching session',
      outputDocument: session.coaching_templates?.output_document || undefined,
      readinessScore: session.readiness_score,
      strengths: toStringArray(scoring.strengths),
      areasForImprovement: toStringArray(scoring.areas_for_improvement),
      keyTakeaways: toStringArray(scoring.key_takeaways),
      goals: structuredMemory.goals,
      commitments: structuredMemory.commitments,
      unresolvedActions: structuredMemory.unresolvedActions,
      summary: extractDocumentSummary(session.document_content),
      isSameCoach: Boolean(currentAgentId && session.agent_id === currentAgentId),
      isSameTemplate: Boolean(currentTemplateId && session.template_id === currentTemplateId)
    };
  });

  return {
    learnerName,
    totalSessionsFound: rows.length,
    items
  };
}

export function buildCoachingMemoryPrompt(memory: CoachingMemoryProfile): string {
  const learnerName = memory.learnerName || 'there';

  if (memory.items.length === 0) {
    return `

LEARNER CONTEXT:
Learner name: ${learnerName}
This is the learner's first session with you. You have no prior session history to reference.

Rules:
- Greet the learner naturally using the configured opening.
- Do NOT invent or claim to remember previous sessions, scores, or topics.
- Do NOT reference "last time", "previously", or any past interaction.
- Do NOT name the output template/document (e.g. "Session Summary") to the learner. That is an internal name for the report generated after the session.
- Move straight into today's coaching objective and intake script.`;
  }

  const sessionLines = memory.items.map((item, index) => {
    const score = typeof item.readinessScore === 'number' ? ` Readiness score: ${item.readinessScore}%.` : '';
    const strengths = item.strengths.length ? ` Strengths: ${item.strengths.join('; ')}.` : '';
    const improvements = item.areasForImprovement.length ? ` Areas to build: ${item.areasForImprovement.join('; ')}.` : '';
    const takeaways = item.keyTakeaways.length ? ` Takeaways: ${item.keyTakeaways.join('; ')}.` : '';
    const goals = item.goals.length ? ` Goals: ${item.goals.join('; ')}.` : '';
    const commitments = item.commitments.length ? ` Commitments: ${item.commitments.join('; ')}.` : '';
    const unresolved = item.unresolvedActions.length ? ` Unresolved actions: ${item.unresolvedActions.join('; ')}.` : '';
    return `${index + 1}. ${item.sessionDate}.${score}${strengths}${improvements}${takeaways}${goals}${commitments}${unresolved}`;
  }).join('\n');

  return `

LEARNER CONTEXT AND MEMORY:
Learner name: ${learnerName}
Prior sessions with THIS coach: ${memory.items.length}
${sessionLines}

Rules for using memory:
- Available for context only. Reference prior work ONLY IF it is directly relevant to what the learner is doing right now, and only after they have started the conversation.
- Do NOT open with "Last time we..." unless the learner asks about continuity or you have a very specific, recent skill area to build on from the list above.
- Do NOT name the output template/document (e.g. "Session Summary") to the learner. That is an internal name for the report generated after the session.
- Do not invent scores, sessions, or details not listed above. Do not reference other coaches' sessions — memory here is scoped to your own prior sessions with this learner only.`;
}

// Returns the exact opening line the coach should speak. Rules:
//   - If the coach agent has a configured `first_message`, that IS the opening,
//     with only name-placeholder substitution ({{name}} etc). No memory sentence,
//     no session-name interpolation, no auto-prefix.
//   - If there is no configured opening, return a short neutral greeting.
//   - Memory (if any) is exposed to the coach via the system prompt only. The
//     coach may choose to reference prior context naturally later in the
//     conversation — but must never be forced to open with "Last time we..."
//     because that hallucinates when memory is stale or scoped wrong.
export function buildCoachingFirstMessage(options: {
  configuredFirstMessage?: string | null;
  learnerName: string;
  memory: CoachingMemoryProfile;
  sessionName?: string | null;
}): string {
  const learnerName = options.learnerName || 'there';
  const configured = stripMemoryHtml(options.configuredFirstMessage || '');

  if (configured) {
    return configured
      .replace(/\{\{\s*(user_name|learner_name|name)\s*\}\}/gi, learnerName)
      .replace(/\{(user_name|learner_name|name)\}/gi, learnerName);
  }

  return `Hi ${learnerName}.`;
}
